import React, { useState } from 'react'
import { QuickLink, QuickLinkFormData } from '../../types'
import { Button } from '../ui'
import { pinBoardApi } from '../../services/api'

interface QuickLinksSectionProps {
  quickLinks: QuickLink[]
  onQuickLinksChange: () => void
}

export const QuickLinksSection: React.FC<QuickLinksSectionProps> = ({
  quickLinks,
  onQuickLinksChange
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingLink, setEditingLink] = useState<QuickLink | null>(null)
  const [formData, setFormData] = useState<QuickLinkFormData>({
    label: '',
    url: '',
    color: 'blue',
    icon: ''
  })
  const [saving, setSaving] = useState(false)

  const colorOptions = [
    { value: 'blue', label: 'Blue', bgClass: 'bg-blue-500', hoverClass: 'hover:bg-blue-600' },
    { value: 'sky', label: 'Sky Blue', bgClass: 'bg-sky-500', hoverClass: 'hover:bg-sky-600' },
    { value: 'green', label: 'Green', bgClass: 'bg-green-500', hoverClass: 'hover:bg-green-600' },
    { value: 'purple', label: 'Purple', bgClass: 'bg-purple-500', hoverClass: 'hover:bg-purple-600' },
    { value: 'orange', label: 'Orange', bgClass: 'bg-orange-500', hoverClass: 'hover:bg-orange-600' },
    { value: 'red', label: 'Red', bgClass: 'bg-red-500', hoverClass: 'hover:bg-red-600' },
    { value: 'gray', label: 'Gray', bgClass: 'bg-gray-500', hoverClass: 'hover:bg-gray-600' }
  ]

  const resetForm = () => {
    setFormData({
      label: '',
      url: '',
      color: 'blue',
      icon: ''
    })
    setShowAddForm(false)
    setEditingLink(null)
  }

  const handleEdit = (link: QuickLink) => {
    setFormData({
      label: link.label,
      url: link.url,
      color: link.color,
      icon: link.icon || ''
    })
    setEditingLink(link)
    setShowAddForm(true)
  }

  const handleSave = async () => {
    if (!formData.label.trim() || !formData.url.trim()) {
      alert('Please fill in both label and URL')
      return
    }

    try {
      setSaving(true)
      
      const payload = {
        label: formData.label.trim(),
        url: formData.url.trim(),
        color: formData.color,
        icon: formData.icon?.trim() || undefined
      }

      if (editingLink) {
        await pinBoardApi.updateQuickLink(editingLink.id, payload)
      } else {
        await pinBoardApi.createQuickLink(payload)
      }

      resetForm()
      onQuickLinksChange()
    } catch (error) {
      console.error('Failed to save quick link:', error)
      alert('Failed to save quick link. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (linkId: number) => {
    if (!window.confirm('Are you sure you want to delete this quick link?')) {
      return
    }

    try {
      await pinBoardApi.deleteQuickLink(linkId)
      onQuickLinksChange()
    } catch (error) {
      console.error('Failed to delete quick link:', error)
      alert('Failed to delete quick link. Please try again.')
    }
  }

  const getButtonStyles = (color: string) => {
    const colorOption = colorOptions.find(opt => opt.value === color)
    if (!colorOption) return 'bg-blue-500 hover:bg-blue-600'
    return `${colorOption.bgClass} ${colorOption.hoverClass}`
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Quick Links</h3>
        <Button
          onClick={() => setShowAddForm(true)}
          size="sm"
          variant="primary"
          disabled={showAddForm}
        >
          + Add Link
        </Button>
      </div>

      {/* Quick Links Display */}
      <div className="mb-4">
        {quickLinks.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">
            No quick links yet. Add some helpful resource links!
          </p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {quickLinks.map((link) => (
              <div key={link.id} className="group relative">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`
                    inline-flex items-center px-4 py-2 rounded-lg text-white font-medium 
                    transition-colors duration-200 ${getButtonStyles(link.color)}
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50
                  `}
                >
                  {link.icon && (
                    <span className="mr-2">{link.icon}</span>
                  )}
                  {link.label}
                </a>
                
                {/* Edit/Delete buttons on hover */}
                <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEdit(link)}
                      className="w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50"
                      title="Edit"
                    >
                      <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(link.id)}
                      className="w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50"
                      title="Delete"
                    >
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-3">
            {editingLink ? 'Edit Quick Link' : 'Add Quick Link'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label *
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Mining News, Company Portal"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL *
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                {colorOptions.map((color) => (
                  <option key={color.value} value={color.value}>
                    {color.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon (optional)
              </label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="📊 🏢 📈 (emoji or text)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex space-x-3 mt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              loading={saving}
              variant="primary"
              size="sm"
            >
              {editingLink ? 'Update' : 'Add'} Link
            </Button>
            <Button
              onClick={resetForm}
              disabled={saving}
              variant="secondary"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
